import { useCallback, useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { fetchDefaultDates, fetchFraudWidgetData } from '../service';
import { NETWORK_CHART_CONFIG, NETWORK_SUMMARY_CONFIG, REJECTION_METHODS_CONFIG, TOTALS_CONFIG } from '../consts';

export function useDefaultDates() {
    // Start with null so no data hooks fire before we know the real report dates.
    // Without this, the initial render fires 4 requests with moment() fallback dates,
    // then fetchDefaultDates resolves and cancels them all, causing 4 canceled + 4 real requests.
    const [dates, setDates] = useState({ startDate: null, endDate: null });

    useEffect(() => {
        fetchDefaultDates()
            .then(value => {
                setDates({ startDate: value.start_date, endDate: value.end_date });
            })
            .catch(() => {
                setDates({
                    startDate: moment()
                        .subtract(6, 'days')
                        .format('YYYY-MM-DD'),
                    endDate: moment().format('YYYY-MM-DD'),
                });
            });
    }, []);

    // setBothDates replaces start + end in a single setState call — required for callers
    // that live outside a React synthetic event (e.g. Bootstrap/jQuery datepicker onApply),
    // where React 15 does not automatically batch two separate setState calls.
    const setBothDates = useCallback((startDate, endDate) => setDates({ startDate, endDate }), []);

    return { startDate: dates.startDate, endDate: dates.endDate, setBothDates };
}

export function useTotalsData(startDate, endDate) {
    const [state, setState] = useState({ data: null, loading: false, error: null });
    useEffect(() => {
        let cancelled = false;
        if (startDate && endDate) {
            setState({ data: null, loading: true, error: null });
            fetchFraudWidgetData({ ...TOTALS_CONFIG, startDate, endDate, channel: 'fraud-totals' })
                .then(data => {
                    if (!cancelled) setState({ data, loading: false, error: null });
                })
                .catch(err => {
                    if (!cancelled) setState({ data: null, loading: false, error: err });
                });
        }
        return () => {
            cancelled = true;
        };
    }, [startDate, endDate]);
    return state;
}

export function useNetworkSummaryData(startDate, endDate) {
    const [state, setState] = useState({ data: null, loading: false, error: null });
    useEffect(() => {
        let cancelled = false;
        if (startDate && endDate) {
            setState({ data: null, loading: true, error: null });
            fetchFraudWidgetData({ ...NETWORK_SUMMARY_CONFIG, startDate, endDate, channel: 'fraud-network' })
                .then(data => {
                    if (!cancelled) setState({ data, loading: false, error: null });
                })
                .catch(err => {
                    if (!cancelled) setState({ data: null, loading: false, error: err });
                });
        }
        return () => {
            cancelled = true;
        };
    }, [startDate, endDate]);
    return state;
}

export function useNetworkChartData(startDate, endDate, timeBreakdown = 'day') {
    const cache = useRef({});
    const [state, setState] = useState({ data: null, loading: false, error: null });
    useEffect(() => {
        let cleanup;
        if (startDate && endDate) {
            const key = `${startDate}|${endDate}|${timeBreakdown}`;
            if (cache.current[key]) {
                setState({ data: cache.current[key], loading: false, error: null });
            } else {
                let cancelled = false;
                setState({ data: null, loading: true, error: null });
                fetchFraudWidgetData({
                    ...NETWORK_CHART_CONFIG,
                    time_breakdown: [timeBreakdown],
                    startDate,
                    endDate,
                    channel: 'fraud-network-chart',
                })
                    .then(data => {
                        if (!cancelled) {
                            cache.current[key] = data;
                            setState({ data, loading: false, error: null });
                        }
                    })
                    .catch(err => {
                        if (!cancelled) setState({ data: null, loading: false, error: err });
                    });
                cleanup = () => {
                    cancelled = true;
                };
            }
        }
        return cleanup;
    }, [startDate, endDate, timeBreakdown]);
    return state;
}

export function useRejectionMethodsData(startDate, endDate) {
    const [state, setState] = useState({ data: null, loading: false, error: null });
    useEffect(() => {
        let cancelled = false;
        if (startDate && endDate) {
            setState({ data: null, loading: true, error: null });
            fetchFraudWidgetData({ ...REJECTION_METHODS_CONFIG, startDate, endDate, channel: 'fraud-rejection' })
                .then(data => {
                    if (!cancelled) setState({ data, loading: false, error: null });
                })
                .catch(err => {
                    if (!cancelled) setState({ data: null, loading: false, error: err });
                });
        }
        return () => {
            cancelled = true;
        };
    }, [startDate, endDate]);
    return state;
}
